import { Locale } from 'locale-enum';
import { map, orderBy, sortBy, startCase, toLower, groupBy, isEmpty, chunk, uniq, concat, compact, flatMap } from 'lodash';
import moment from 'moment';

import { InspectionReportType, ReportScheduleTypeEnum } from '@pimm/services/lib/sms-tenants';
import {
  CorrectiveAction,
  DmReview,
  Form,
  FormNote,
  GetAttachmentContentResponseDto,
  ItemStatusEnum,
  LineItemTag,
  OpsLeaderAssignmentDto,
  ReportDay,
  ReportNote,
  SectionData,
  Site,
  StoreReportDataDto,
  StringTranslation,
} from '@pimm/services/lib/store-inspections';
import { FormInputSources } from '../consts';
import { Review } from '../types';
import { getInputDisplay } from '../_helper';
import translations from '../localization/resources';
import { EquipmentName } from './equipment-name';
import { InputTypeEnum } from '@pimm/services/lib/sms-formpack/types';
import { formatTo } from '@app/utils/date-formatter';

export enum StoreReportType {
  CriticalControlPoints = 'CriticalControlPoints',
  CriticalWalkthrough = 'FSCriticalWalkthrough',
  DailyFsl = 'DailyFsl',
  DailyOpsChecklist = 'DailyOpsChecklist',
}

interface Titled {
  title?: string;
  titleTranslations?: StringTranslation[];
  subTitle?: string;
  subTitleTranslations?: StringTranslation[];
}

export namespace BaseReport {
  export interface User {
    userId?: string;
    username?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    title?: string;
  }

  export interface ReviewInfo {
    userId?: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    title?: string;
    timestampUtc?: string;
  }

  export interface Note {
    attachmentCount?: number;
    attachmentIds?: string[];
    clientTimestamp?: string;
    daypart?: number;
    includeInReport?: boolean;
    note?: string;
    noteId?: number;
    sectionLineItemId?: number;
    user?: User;
  }

  export interface Input {
    correctiveActions?: string[];
    inputSource?: string;
    clientTimestamp?: string;
    numericReading?: number;
    textReading?: string;
    user?: User;
    tagId?: number;
  }

  export interface Item extends Titled {
    seq?: number;
    sectionLineItemId?: number;
    lineItemId?: string;
    isDynamicTitle?: boolean;
    inputType?: InputTypeEnum;
    standard?: string;
    standardTranslations?: StringTranslation[];
    status: ItemStatusEnum;
    tag?: LineItemTag;
    inputs: Record<number, Input[]>;
    // section?: Section;
    // example:
    // {
    //   [1]: { [1]: { name: 'Daypart 1', ... }, [2]: { name: 'Daypart 1', ... } },
    //   [2]: { [1]: { name: 'Daypart 2', ... } },
    // }
    // dayparts?: Record<number, Record<number, Input>>;
  }

  export interface Section extends Titled {
    packageDetailsSectionId?: string;
    data?: Item[];
  }

  export interface Form extends Titled {
    seq?: number;
    notes?: Note[];
    packageDetailsFormId?: string;
    sections?: Section[];
    storeFormId?: string;
  }

  export interface FormGroup extends Titled {
    seq?: number;
    packageDetailsGroupId?: string;
    formGroupId?: string;
    forms?: Form[];
  }

  export interface Daypart {
    partNumber?: number;
    label?: string;
    translations?: StringTranslation[];
    formGroups?: FormGroup[];
    opsLeader?: ReviewInfo;
  }

  export interface ReportDay {
    date: string;
    dayparts: Daypart[];
    eodReview?: ReviewInfo;
    dmReview?: ReviewInfo;
    notes?: Note[];
  }

  export interface Data extends Titled {
    storeReportId?: string;
    date?: string;
    name?: string;
    module?: string;
    lastModifiedOnUtc?: string;
    formpackEffectiveDate?: string;
    reportDays: ReportDay[];
    reportType?: Partial<InspectionReportType>;
  }
}

export type ComposerSchema<TData = unknown> = TData & {
  date: string;
  correctiveActions?: { code: string; name: string }[];
  report: {
    title: string;
    caption?: string;
    footNote?: string;
  };
  site: Site;
  reviews: any;
  notes: any;
  formGroups: any;
  translations: any;
};

interface IAuthor {
  id?: string;
  username?: string;
  name?: string;
  title?: string;
}

export interface ILeader extends IAuthor {
  timestamp?: string;
}

interface ITitled {
  title?: string;
  titleTranslations?: StringTranslation[];
  subTitle?: string;
  subTitleTranslations?: StringTranslation[];
}

interface IAttachment {
  attachmentData?: string;
  attachmentUrl?: string;
  contentType?: string;
  name?: string;
}

export interface INote {
  attachmentCount?: number;
  attachmentIds?: string[];
  clientTimestamp?: string;
  clientTimestampUtc?: string;
  note?: string;
  noteId?: number;
  lineItemId?: string;
  sectionLineItemId?: number;
  storeFormId?: string;
  user?: IAuthor;

  // Custom Title
  attachments?: IAttachment[];
  sectionTitle?: string;
  lineItemTitle?: string;
}

export interface IInput {
  clientTimestamp?: string;
  clientTimestampUtc?: string;
  correctiveActions?: string[];
  inputId?: number;
  inputSource?: string;
  lineItemId?: string;
  numericReading?: number;
  sectionLineItemId?: number;
  textReading?: string;
  user?: IAuthor;
}

export interface IItem extends ITitled {
  inputType?: InputTypeEnum;
  isDynamicTitle?: boolean;
  lineItemId?: string;
  status?: ItemStatusEnum;
  sectionLineItemId?: number;
  standard?: string;
  standardTranslations?: StringTranslation[];
  inputs?: Record<string, IInput>;
  tag?: ITag;
}

export interface ISection extends ITitled {
  packageDetailsSectionId?: string;
  seq?: string;
  data: IItem[];
}

export interface IForm extends ITitled {
  seq?: number;
  packageDetailsFormId?: string;
  formId?: string;
  sections: ISection[];
  startDaypart?: number;
  endDaypart?: number;
  notes?: INote[];
  storeFormId?: string;
}

export interface IFormGroup extends ITitled {
  packageDetailsGroupId?: string;
  seq?: number;
  forms: IForm[];
}

export interface IDaypart {
  partNumber?: number;
  label?: string;
  labelTranslations?: StringTranslation[];
  formGroups: IFormGroup[];
  review?: ILeader;
}

export interface ITag extends ITitled {
  id?: number;
}

export interface IReportDate {
  date?: string;
  dayparts: IDaypart[];
  notes: INote[];
  eodReview?: ILeader;
}

export interface IStoreReportComposer extends ITitled {
  date?: string;
  name?: string;
  module?: string;
  lastModifiedOnUtc?: string;
  reportType?: Partial<InspectionReportType>;
  storeReportId?: string;
  reportDates: IReportDate[];
  reviewers?: {
    dayparts?: (ITitled & {
      ospLeader?: ILeader;
    })[];
    generalManager?: ILeader;
    districtManager?: ILeader;
  };
}

interface TextTranslations {
  culture?: string;
  text?: string;
}

// Base abstract class
export abstract class StoreFormReport {
  locale: Locale = Locale.en_US;

  attachments?: Record<string, GetAttachmentContentResponseDto>;
  attachmentsIds?: { id: string; type: 'form' | 'report' }[];
  correctiveActions?: CorrectiveAction[];
  reportData?: BaseReport.Data;
  site?: Site;
  /**
   * Version of the report template used to render this report.
   * Mirrors `reportData.reportTemplate?.version` from the backend DTO.
   */
  reportTemplateVersion?: string;
  isLandscape = false;

  constructor(_report: StoreReportDataDto) {
    this.attachments = {};
    this.attachmentsIds = [];
    this.correctiveActions = _report.reportData?.correctiveActions;
    this.site = _report.reportData?.site;

    // Capture the template version for version-specific composer behaviour (e.g. FSL v1.3+)
    this.reportTemplateVersion = _report.reportData?.reportData?.reportTemplate?.version;

    const reportData = _report.reportData?.reportData;
    const scheduleType = (reportData?.scheduleType ?? ReportScheduleTypeEnum.None) as ReportScheduleTypeEnum;
    let reportDays: BaseReport.ReportDay[] = [];

    for (const _reportDay of reportData?.reportDays ?? []) {
      const normalizedDay = this.extractReportByDate(_reportDay);
      const flattenForms = flatMap(_reportDay.dayparts, ({ formGroups = [] }) => compact(formGroups.flatMap(_ => _.forms)));
      const formNotes = flattenForms.flatMap(({ notes = [] }) => compact(notes.filter(_ => _.includeInReport)));
      // prettier-ignore
      const reportNotes = compact(
        concat(_reportDay.reportNotes ?? [], flatMap(_reportDay.dayparts, _ => _.reportNotes)),
      );

      // Append DmReview for each day
      normalizedDay.dmReview = this.extractLeader(reportData?.dmReview);
      // prettier-ignore
      normalizedDay.notes = reportNotes.map(this.extractNote);

      // Get all attachmentIds for all report notes
      for (const { attachmentIds = [], includeInReport } of reportNotes) {
        if (!includeInReport || !attachmentIds) continue;
        attachmentIds?.forEach(_id => {
          this.attachmentsIds!.push({ id: _id, type: 'report' });
        });
      }

      // Get all attachmentIds for all form notes
      for (const { attachmentIds = [], includeInReport } of formNotes) {
        if (!includeInReport || !attachmentIds) continue;
        attachmentIds.forEach(_id => {
          this.attachmentsIds!.push({ id: _id, type: 'form' });
        });
      }

      reportDays.push(normalizedDay);
    }

    // Extract all attachmentIds that needs to be included in this report
    reportDays.flatMap(({ dayparts, notes = [] }) => {
      const flattenForms = dayparts.flatMap(({ formGroups = [] }) => compact(formGroups.flatMap(_ => _.forms)));
      return [
        ...notes.filter(_ => _.includeInReport).flatMap(_ => compact(_.attachmentIds)),
        ...flattenForms.flatMap(({ notes = [] }) => compact(notes.filter(_ => _.includeInReport).flatMap(_ => _.attachmentIds))),
      ];
    });

    // Normalize StoreReport
    this.reportData = {
      storeReportId: reportData?.storeReportId,
      date: reportData?.date,
      name: reportData?.name,
      module: reportData?.module,
      lastModifiedOnUtc: _report.lastModifiedOnUtc,
      reportDays: reportDays,
      reportType: {
        reportId: reportData?.reportId,
        reportType: reportData?.scheduleType,
        reportScheduleType: scheduleType,
      },
      subTitle: reportData?.subTitle,
      subTitleTranslations: reportData?.subTitleTranslations,
      formpackEffectiveDate: reportData?.formpackEffectiveDate,
      title: reportData?.title,
      titleTranslations: reportData?.titleTranslations,
    };
  }

  private extractItem = (_item: SectionData) => {
    // Handle the inputs structure from the JSON data
    const inputs = Object.entries(_item.inputs ?? {}).reduce((inputs: Record<number, BaseReport.Input[]>, [inputIdStr, _inputs]) => {
      const inputId = Number(inputIdStr);
      if (!isNaN(inputId) && Array.isArray(_inputs) && _inputs.length > 0) {
        const sortedInputs = orderBy(_inputs, ['clientTimestampUtc'], ['desc']);

        return {
          ...inputs,
          [inputId]: sortedInputs.map(({ user, ..._input }) => {
            const input: BaseReport.Input = {
              correctiveActions: _input.correctiveActions,
              inputSource: _input.inputSource,
              clientTimestamp: _input.clientTimestamp,
              numericReading: _input.numericReading,
              textReading: _input.textReading,
            };

            if (user?.userId) {
              const [username, firstName, lastName] = user.name?.split('$') ?? [];
              const fullName = startCase(toLower([firstName, lastName].filter(Boolean).join(' ')));
              input.user = {
                userId: user.userId,
                title: user.title,
                name: fullName,
                username,
                firstName,
                lastName,
              };
            }

            return input;
          }),
        };
      }
      return inputs;
    }, {});

    const item: BaseReport.Item = {
      inputType: this.extractInputType(_item?.inputType),
      lineItemId: _item.lineItemId,
      sectionLineItemId: _item.sectionLineItemId,
      isDynamicTitle: _item.isDynamicTitle,
      standard: _item.standard,
      standardTranslations: _item.standardTranslations,
      status: _item.status ?? ItemStatusEnum.NotStarted,
      tag: _item.tag,
      title: _item.title,
      titleTranslations: _item.titleTranslations,
      inputs: inputs,
    };

    return item;
  };

  private extractForm = (_form: Form) => {
    const sortedSections = sortBy(_form.sections, ['seq']);
    const sections: BaseReport.Section[] = [];

    for (const _section of sortedSections) {
      const section: BaseReport.Section = {
        packageDetailsSectionId: _section.packageDetailsSectionId,
        title: _section.title,
        titleTranslations: _section.titleTranslations,
        subTitle: _section.subTitle,
        subTitleTranslations: _section.subTitleTranslations,
        data: sortBy(_section.data, ['seq']).map(this.extractItem),
      };

      sections.push(section);
    }

    const form: BaseReport.Form = {
      seq: _form.seq,
      storeFormId: _form.storeFormId,
      notes: _form.notes?.map(this.extractNote),
      packageDetailsFormId: _form.packageDetailsFormId,
      title: _form.title,
      titleTranslations: _form.titleTranslations,
      subTitle: _form.subTitle,
      subTitleTranslations: _form.subTitleTranslations,
      sections,
    };

    return form;
  };

  private extractReportByDate = (_reportDay: ReportDay) => {
    const dayparts = _reportDay.dayparts?.map(_daypart => {
      const sortedFormGroups = sortBy(_daypart.formGroups, ['seq']);
      const daypart: BaseReport.Daypart = {
        partNumber: _daypart.partNumber,
        label: _daypart.label,
        translations: _daypart.translations,
        opsLeader: this.extractLeader(_daypart.review ?? _daypart.opsLeader),
        formGroups: sortedFormGroups.map(({ forms, ..._formGroup }) => {
          const formGroup: BaseReport.FormGroup = {
            ..._formGroup,
            forms: sortBy(forms).map(this.extractForm),
          };
          return formGroup;
        }),
      };

      return daypart;
    });

    const reportDay: BaseReport.ReportDay = {
      date: _reportDay.date!,
      dayparts: dayparts ?? [],
      eodReview: this.extractLeader(_reportDay.eodReview ?? _reportDay.eodManager),
    };
    return reportDay;
  };

  private extractInputType(value?: string): InputTypeEnum | undefined {
    if (!value) return undefined;
    return Object.values(InputTypeEnum).includes(value as InputTypeEnum) ? (value as InputTypeEnum) : undefined;
  }

  private extractLeader(_leader?: Review & DmReview & OpsLeaderAssignmentDto): Partial<BaseReport.ReviewInfo> {
    if (!_leader) return {};

    const name = _leader.reviewerUserName ?? _leader.userName ?? _leader.name;
    const [username, firstName, lastName] = name?.split?.('$') ?? [];
    const fullName = startCase(toLower([firstName, lastName].filter(Boolean).join(' ')));
    return {
      userId: _leader.reviewerUserId ?? _leader.userId,
      name: isEmpty(fullName) ? username : fullName,
      title: _leader.reviewerTitle ?? _leader.title,
      timestampUtc: _leader.reviewerTimestampUtc ?? _leader.reviewTimestampUtc,
      username,
    };
  }

  private extractNote(_note: FormNote & ReportNote): BaseReport.Note {
    const note: BaseReport.Note = {
      attachmentCount: _note.attachmentCount,
      attachmentIds: _note.attachmentIds,
      clientTimestamp: _note.clientTimestamp,
      daypart: _note.daypart,
      includeInReport: _note.includeInReport,
      note: _note.note,
      noteId: _note.noteId,
      sectionLineItemId: _note.sectionLineItemId,
    };

    if (_note.user?.userId) {
      const [username, firstName, lastName] = _note.user?.name?.split('$') ?? [];
      note.user = {
        userId: _note.user?.userId,
        title: _note.user?.title,
        name: startCase(toLower([firstName, lastName].filter(Boolean).join(' '))),
        username,
        firstName,
        lastName,
      };
    }

    return note;
  }

  protected chunkAttachments = (attachments?: GetAttachmentContentResponseDto[], chunkSize: number = 3) => {
    try {
      if (!attachments?.length) return [];
      return chunk(attachments, chunkSize).map(group => ({
        rows: group.map(data => {
          const contentType = data?.contentType?.replace(/heic/gi, 'jpg') || 'image/jpeg';
          const rawData = data?.attachmentData ?? ''; // handles null/undefined safely
          const attachmentData = typeof rawData === 'string' ? rawData : '';
          return { data: attachmentData ? `data:${contentType};base64,${attachmentData}` : '' };
        }),
      }));
    } catch (e) {
      return [];
    }
  };

  private translateDescription = (_item: Partial<BaseReport.Item>) => {
    let description = this.translate(_item.title, _item.titleTranslations);

    if (_item.isDynamicTitle) {
      const { name: equipmentName, suffix } = new EquipmentName(_item.title);
      if (equipmentName) {
        description = [this.translate(`store_equipment:${equipmentName}`), suffix].filter(Boolean).join(' ');
      }
    }
    return description;
  };

  //#region Composer

  protected composerBody(): ComposerSchema {
    const scheduleType = this.reportData?.reportType?.reportScheduleType;
    const reportTitle = this.translate(this.reportData?.title, this.reportData?.titleTranslations);
    const reportCaption = this.translate(this.reportData?.subTitle, this.reportData?.subTitleTranslations);
    const lastModifiedDate = moment(this.reportData?.formpackEffectiveDate).local();
    let reportDate = '';

    if (this.reportData?.date) {
      reportDate = moment(this.reportData.date).format('ddd, MM/DD/YYYY');

      if (scheduleType === ReportScheduleTypeEnum.Weekly) {
        const startDate = moment(this.reportData.date);
        const endDate = startDate.clone().add(6, 'day');
        reportDate = `${startDate.format('ddd, MM/DD/YYYY')} - ${endDate.format('ddd, MM/DD/YYYY')}`;
      }
    }

    return {
      date: reportDate,
      reviews: {},
      notes: [],
      formGroups: {},
      translations: {},
      site: {
        id: this.site?.id,
        name: this.site?.name,
      },
      report: {
        title: reportTitle,
        caption: reportCaption,
        footNote: `${reportTitle} - Rev. ${lastModifiedDate.format('MM/DD/YYYY')}`,
      },
      correctiveActions: map(
        this.correctiveActions?.filter(_ => !!_.code),
        _ => ({ code: _.code!, name: this.translate(_.name, _?.nameTranslations) }),
      ).sort((a, b) => a.code.localeCompare(b.code)),
    };
  }

  // each report must implement how its body is generated
  abstract composeReport(locale: Locale): ComposerSchema;

  protected composeReviewer({ label, ..._leader }: Partial<BaseReport.ReviewInfo> & { useDefId?: string; label?: string }) {
    const timestampUtc = _leader?.timestampUtc;
    // TODO: Fix composer type check
    const opsLeader: Record<string, string | undefined> = {
      useDefId: _leader.useDefId,
      label: label ?? '',
      name: _leader.name,
      icon: timestampUtc ? 'approveIcon' : undefined,
      timestamp: timestampUtc ? this.utcToFormat(timestampUtc) : undefined,
    };
    return opsLeader;
  }

  protected composeInput(_inputs?: BaseReport.Input[], _inputType?: InputTypeEnum) {
    if (!_inputs?.length) return undefined;

    let correctiveActions: string[] = [];
    let textReadings: string[] = [];
    let inputIcons: string[] = [];
    let timestamp: string | undefined;

    _inputs.forEach(({ clientTimestamp, inputSource, ..._input }) => {
      correctiveActions = concat(correctiveActions, _input.correctiveActions ?? []);
      let text = getInputDisplay(_input, _inputType);

      if (inputSource) {
        const source = FormInputSources.find(_ => inputSource.toLowerCase().includes(_.keyword));
        if (source) inputIcons.push(source.keyword);
      }

      if (clientTimestamp) {
        // Get the latest timestamp
        timestamp = [timestamp, clientTimestamp].filter(Boolean).sort((a, b) => (a! > b! ? -1 : 1))[0];
      }

      if (text) {
        if (_inputType === InputTypeEnum.YesNoAndNa || _inputType === InputTypeEnum.YesNo) {
          text = this.translate(text?.toLowerCase().replace('/', ''));
        }
        textReadings.push(text);
      }
    });

    return {
      correctiveActions: uniq(correctiveActions).sort((a, b) => (a > b ? -1 : 1)),
      // This is to make sure that we use the correct icon base on the given hierarchy
      inputSource: inputIcons.length > 0 ? FormInputSources.find(_ => inputIcons.includes(_.keyword))?.icon : undefined,
      timestamp: timestamp ? formatTo(timestamp, 'hh:mm A|MM/DD/YYYY')?.split('|').join('\n') : '',
      value: textReadings.join(', '),
    };
  }

  protected composeItem(_item: Partial<BaseReport.Item>) {
    return {
      description: this.translateDescription(_item),
      standard: this.translate(_item.standard, _item.standardTranslations),
    };
  }

  protected composeNote(_note: BaseReport.Note) {
    const timestamp = _note?.clientTimestamp;
    return {
      useDefId: 'noteBox',
      client: { name: _note.user?.name ?? '', role: _note.user?.title ?? '' },
      timeStamp: timestamp ? formatTo(timestamp, 'hh:mm A MM/DD/YYYY') : '',
      note: _note.note || '',
    };
  }

  protected composeReportNotes(_report: BaseReport.ReportDay, _title?: string) {
    const mapNotesByDaypart = groupBy(
      _report.notes?.filter(_ => _.includeInReport),
      'daypart',
    );
    const endOfDayNotes = mapNotesByDaypart['null'];
    const defs: any[] = [];

    if (!isEmpty(endOfDayNotes)) {
      const daypartName = this.translate('end_of_day');
      defs.push({ useDefId: 'noteHeader', title: _title ?? daypartName, daypart: !!_title ? daypartName : '' });
      endOfDayNotes.forEach(_note => defs.push(this.composeNote(_note)));
    }

    for (const { partNumber = 1, ..._daypart } of _report.dayparts) {
      const daypartName = this.translate(_daypart.label, _daypart.translations);
      const notes = mapNotesByDaypart[partNumber];

      if (!isEmpty(notes)) {
        defs.push({ useDefId: 'noteHeader', title: _title ?? daypartName, daypart: !!_title ? daypartName : '' });

        notes.forEach(_note => {
          defs.push(this.composeNote(_note));

          if (!isEmpty(this.attachments)) {
            const attachments = compact(_note.attachmentIds).map(_id => this.attachments![_id]);
            this.chunkAttachments(attachments).forEach(_attachments => {
              defs.push({ useDefId: this.isLandscape ? 'attachmentBoxLandScape' : 'attachmentBox', ..._attachments });
            });
          }
        });
      }
    }

    return defs;
  }

  protected composeFormNotes(_form: BaseReport.Form, _daypart?: BaseReport.Daypart) {
    const mapNotesById = groupBy(
      _form.notes?.filter(_ => _.includeInReport),
      'sectionLineItemId',
    );
    const generalNotes = mapNotesById['null'];
    const defs: any[] = [];

    // Form Title
    defs.push({
      useDefId: 'noteHeader',
      title: this.translate(_form.title, _form.titleTranslations),
      daypart: _daypart ? this.translate(_daypart.label, _daypart.translations) : '',
    });

    // General Notes
    if (!isEmpty(generalNotes)) {
      defs.push({ useDefId: 'noteSubHeader', title: this.translate('general_notes') });
      generalNotes.forEach(_note => {
        defs.push(this.composeNote(_note));

        if (!isEmpty(this.attachments)) {
          const attachmentIds = compact(_note.attachmentIds);
          const attachments = attachmentIds.map(_id => this.attachments![_id]).filter(Boolean);
          this.chunkAttachments(attachments).forEach(_attachments => {
            defs.push({ useDefId: this.isLandscape ? 'attachmentBoxLandScape' : 'attachmentBox', ..._attachments });
          });
        }
      });
    }

    // Item Notes
    for (const _section of _form.sections ?? []) {
      for (const _item of _section.data ?? []) {
        const notes = mapNotesById[_item.sectionLineItemId!.toString()];

        if (!isEmpty(notes)) {
          const description = this.translateDescription(_item);

          defs.push({ useDefId: 'noteSubHeader', title: description });

          notes.forEach(_note => {
            defs.push(this.composeNote(_note));

            if (!isEmpty(this.attachments)) {
              const attachmentIds = compact(_note.attachmentIds);
              const attachments = attachmentIds.map(_id => this.attachments![_id]).filter(Boolean);
              this.chunkAttachments(attachments).forEach(_attachments => {
                defs.push({ useDefId: this.isLandscape ? 'attachmentBoxLandScape' : 'attachmentBox', ..._attachments });
              });
            }
          });
        }
      }
    }

    // If we only have the top-level form header and no actual notes,
    // treat this as "no notes" so callers don't render an empty section.
    const hasNotes = defs.some(def => def.useDefId !== 'noteHeader');
    return hasNotes ? defs : [];
  }

  //#endregion

  protected translate(value?: string, _cultures?: TextTranslations[]): string {
    if (!value) return '';
    if (_cultures?.length) {
      return (
        _cultures.find(_ => _.culture?.toLowerCase() === (this.locale ?? Locale.en_US)?.toLowerCase())?.text ??
        _cultures.find(_ => _.culture?.toLowerCase() === Locale.en_US.toLowerCase())?.text ??
        value ??
        ''
      );
    }

    return (translations[this.locale] ?? translations[Locale.en_US])[value] ?? value;
  }

  protected utcToFormat(_timestamp: string, textFormat?: string): string | '' {
    return moment
      .utc(_timestamp)
      .utcOffset(this.site?.address?.utcOffsetInMinutes ?? 0)
      .format(textFormat ?? 'MM/DD/YYYY h:mm A');
  }
}
